import { ReactElement, ReactNode } from 'react';
import {
    FieldError,
    FieldErrorsImpl,
    FieldValues,
    Merge,
    useFormContext,
    useFormState,
    useWatch,
} from 'react-hook-form';
import { get, isEmpty, isNil, omitBy } from 'lodash';
import { findChildByType, getComponentProps } from '@fiverr-private/ui_utils';
import { Types } from '@fiverr-private/theme';
import { FORM_COUNTER_ID, FORM_ELEMENT_ID, FORM_HEADER_ID, FORM_HELPER_TEXT_ID } from '../constants';
import { ControlProps, FormControlProps } from '../types';

interface GetValidationColor {
    isError: ControlProps['isError'];
    isSuccess: ControlProps['isSuccess'];
}

export const getValidationColor = ({ isError, isSuccess }: GetValidationColor) => {
    if (isError) {
        return 'validationError';
    } else if (isSuccess) {
        return 'validationSuccess';
    } else {
        return 'bodySecondary';
    }
};
export const getStylingProps = ({
    isError,
    isSuccess,
}: GetValidationColor): { wrapper: Types.FlexLayoutProps; footer: Types.Box } => {
    const color = getValidationColor({ isError, isSuccess });
    return {
        wrapper: {
            gap: '1.5',
            direction: 'column',
        },
        footer: {
            lineHeight: 'b_sm',
            fontSize: 'b_sm',
            color,
            display: 'flex',
            justifyContent: 'spaceBetween',
            gap: '3',
        },
    };
};

export const getFormElementProps = ({
    children,
    name,
    deps,
}: {
    children: ReactElement;
    name: FieldValues['name'];
    deps: FormControlProps['deps'];
}) => {
    const { register, setValue, control } = useFormContext();
    const { onBlur, onChange, ref } = register(name, { deps });
    const { isSubmitted } = useFormState(name);

    return {
        ref,
        onChange: (value: React.ChangeEvent<HTMLInputElement>) => {
            if (value?.target) {
                onChange?.(value);
            } else {
                setValue(name, value, { shouldValidate: isSubmitted });
            }
            children?.props?.onChange?.(value);
        },
        onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
            children?.props?.onBlur?.(event);
            onBlur?.(event);
        },
        control,
    };
};

export const getChildren = (children: ReactNode) => {
    const FormHeader = findChildByType(children, FORM_HEADER_ID);
    const FormElement = findChildByType(children, FORM_ELEMENT_ID);
    const FormHelperText = findChildByType(children, FORM_HELPER_TEXT_ID);
    const FormCounter = findChildByType(children, FORM_COUNTER_ID);

    return {
        FormHeader,
        FormElement,
        FormHelperText,
        FormCounter,
    };
};

export const useReactHookForm = ({ name, maxLength, isSuccess, isError }: Omit<FormControlProps, 'children'>) => {
    try {
        const { errors: formErrors } = useFormState({ name });
        const errors = get(formErrors, name);
        const value = useWatch({ name });
        const isFormError = Object.keys(errors || {}).length > 0 || isError;

        return {
            errors,
            name,
            value,
            isFormError,
            isSuccess,
            maxLength,
        };
    } catch (e) {
        throw new Error(
            "FormControl must be rendered within a Form. Make sure to provide the 'methods' prop to the Form component"
        );
    }
};

export const shouldRenderFormFooter = ({
    FormHelperText,
    FormCounter,
    errors,
}: {
    FormHelperText: ReactNode;
    FormCounter: ReactNode;
    errors?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
}) => FormCounter || !isEmpty(omitBy(getComponentProps(FormHelperText), isNil)) || !isEmpty(errors);
